import { createFileRoute, Link } from "@tanstack/react-router";
import { useQuery } from "@tanstack/react-query";
import {
  Users,
  Star,
  Building2,
  Layers3,
  ClipboardList,
  BadgeCheck,
  ShieldOff,
} from "lucide-react";
import { DashboardShell } from "@/components/DashboardShell";
import { SUPER_ADMIN_NAV } from "@/lib/nav";
import { fetchPlatformStats } from "@/lib/super-admin";

export const Route = createFileRoute("/_authenticated/super-admin/dashboard")({
  head: () => ({
    meta: [
      { title: "Platform overview · Landoo Super Admin" },
      { name: "description", content: "Live platform statistics for Landoo Super Admins." },
    ],
  }),
  component: DashboardPage,
});

function DashboardPage() {
  const { data, isLoading } = useQuery({
    queryKey: ["super-admin", "stats"],
    queryFn: fetchPlatformStats,
  });

  return (
    <DashboardShell title="Platform overview" role="Super Admin" items={SUPER_ADMIN_NAV}>
      {isLoading || !data ? (
        <div className="rounded-2xl border border-border bg-card p-6 text-sm text-muted-foreground">
          Loading platform statistics…
        </div>
      ) : (
        <>
          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Partners"
              value={data.partners}
              icon={Star}
              link="/super-admin/partners"
            />
            <StatCard
              label="Customers"
              value={data.customers}
              icon={Users}
              link="/super-admin/customers"
            />
            <StatCard
              label="Active services"
              value={data.activeServices}
              icon={Layers3}
              link="/super-admin/services"
            />
            <StatCard
              label="Suspended partners"
              value={data.suspendedPartners}
              icon={ShieldOff}
              link="/super-admin/partners"
              tone="coral"
            />
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            <StatCard
              label="Total listings"
              value={data.listings.total}
              icon={Building2}
              link="/super-admin/listings"
            />
            <StatCard
              label="Pending approval"
              value={data.listings.pending}
              icon={ClipboardList}
              link="/super-admin/listings"
              tone="brand"
            />
            <StatCard
              label="Approved"
              value={data.listings.published}
              icon={BadgeCheck}
              link="/super-admin/listings"
              tone="rent"
            />
            <StatCard
              label="Parking listings"
              value={data.listings.parking}
              icon={Building2}
              link="/super-admin/listings"
              tone="parking"
            />
          </div>

          <section className="mt-6 rounded-2xl border border-border bg-card p-4 shadow-soft">
            <h2 className="text-sm font-bold text-brown-dark">Listings by service</h2>
            <div className="mt-3 grid gap-3 sm:grid-cols-4">
              <MiniStat label="Sale" value={data.listings.sale} chip="bg-sale/15 text-sale" />
              <MiniStat label="Rent" value={data.listings.rent} chip="bg-rent/15 text-rent" />
              <MiniStat label="Lease" value={data.listings.lease} chip="bg-lease/15 text-lease" />
              <MiniStat
                label="Parking"
                value={data.listings.parking}
                chip="bg-parking/15 text-parking"
              />
            </div>
          </section>
        </>
      )}
    </DashboardShell>
  );
}

function StatCard({
  label,
  value,
  icon: Icon,
  link,
  tone = "default",
}: {
  label: string;
  value: number;
  icon: React.ComponentType<{ className?: string }>;
  link: string;
  tone?: "default" | "coral" | "brand" | "rent" | "parking";
}) {
  const toneClass = {
    default: "bg-secondary text-brown-dark",
    coral: "bg-coral/15 text-coral",
    brand: "bg-brand/25 text-brown-dark",
    rent: "bg-rent/15 text-rent",
    parking: "bg-parking/15 text-parking",
  }[tone];
  return (
    <Link
      to={link}
      className="rounded-2xl border border-border bg-card p-4 shadow-soft transition hover:shadow-md"
    >
      <div className={`inline-flex h-9 w-9 items-center justify-center rounded-xl ${toneClass}`}>
        <Icon className="h-4 w-4" />
      </div>
      <div className="mt-3 text-2xl font-black text-brown-dark">{value.toLocaleString()}</div>
      <div className="text-xs font-semibold text-muted-foreground">{label}</div>
    </Link>
  );
}

function MiniStat({ label, value, chip }: { label: string; value: number; chip: string }) {
  return (
    <div className="rounded-xl border border-border p-3">
      <span
        className={`inline-flex rounded-full px-2 py-0.5 text-[10px] font-bold uppercase ${chip}`}
      >
        {label}
      </span>
      <div className="mt-2 text-xl font-black text-brown-dark">{value.toLocaleString()}</div>
    </div>
  );
}
